import { ApiError } from './client';
import { saveNotebook } from './endpoints';
import type { NotebookDto } from '@/types/api';

/**
 * Write-through persistence for notebooks, with recovery from an unusable id.
 *
 * Notebook ids are client-generated, so they can collide with something the
 * server already knows:
 *
 *   403 — the id belongs to a different principal. The server is right to
 *         refuse: silently overwriting another user's notebook would be far
 *         worse. This happens routinely when a browser carries an id minted
 *         under an earlier principal (say, before OIDC was enabled).
 *   404 — the notebook was deleted elsewhere.
 *
 * In both cases the *id* is unusable but the user's work is not, and there is
 * no reason to lose server persistence over a naming collision. So we mint a
 * fresh id, retry once, and keep every cell.
 *
 * 401 is deliberately excluded: that means re-authenticate, not re-identify,
 * and is handled by the auth layer.
 */

export const REIDENTIFY_STATUSES: ReadonlySet<number> = new Set([403, 404]);

export interface PersistResult {
  readonly saved: NotebookDto;
  /** The old id, when the notebook had to be re-identified to save. */
  readonly reassignedFrom: string | null;
}

export async function persistNotebook(
  dto: NotebookDto,
  mintId: () => string,
): Promise<PersistResult> {
  try {
    return { saved: await saveNotebook(dto), reassignedFrom: null };
  } catch (cause) {
    if (!(cause instanceof ApiError) || !REIDENTIFY_STATUSES.has(cause.status)) {
      throw cause;
    }

    // Same content, new identity. Retried exactly once -- a second failure is
    // a real problem and should surface rather than loop.
    const retried: NotebookDto = { ...dto, id: mintId() };
    const saved = await saveNotebook(retried);
    return { saved, reassignedFrom: dto.id };
  }
}
